import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import AuthLayout from '../../components/layout/AuthLayout';
import { Shield, AlertCircle } from 'lucide-react';
import { Spinner } from '../../components/common/Spinner';

const Verify2FAPage = () => {
    const [token, setToken] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const inputRef = useRef<HTMLInputElement>(null);

    // Read the original location forwarded by LoginPage so we can return the
    // user to where they came from after MFA. Critical for the founder
    // view-as flow: /?viewAs=<companyId> must survive auth + MFA or the
    // client-app dashboard renders the founder's bare `companyId: 'system'`
    // identity instead of the target tenant.
    const fromLocation = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
    const redirectTarget = fromLocation?.pathname
        ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
        : '/';

    // Auto-focus input on mount for better UX
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleVerify = async () => {
        setError('');
        setLoading(true);
        try {
            const verifyLoginMFA = httpsCallable(functions, 'verifyLoginMFA');
            await verifyLoginMFA({ token });
            navigate(redirectTarget, { replace: true });
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && token.length === 6 && !loading) {
            handleVerify();
        }
    };

    return (
        <AuthLayout>
            <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full border border-gray-700">
                {/* Header with gradient accent */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 mb-4">
                        <Shield size={32} className="text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2">Two-Factor Verification</h2>
                    <p className="text-gray-400 text-sm">Enter the code from your authenticator app.</p>
                </div>

                {/* Error Alert */}
                {error && (
                    <div
                        className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg mb-6 flex items-start"
                        role="alert"
                        aria-live="assertive"
                    >
                        <AlertCircle className="mr-3 flex-shrink-0 mt-0.5" size={20} />
                        <span className="text-sm">{error}</span>
                    </div>
                )}

                {/* Input Field */}
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="123456"
                    value={token}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToken(e.target.value)}
                    onKeyPress={handleKeyPress}
                    maxLength={6}
                    className="w-full p-4 mb-6 bg-gray-700/50 border border-gray-600 text-white text-center text-xl tracking-widest rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                    required
                    aria-label="2FA verification code"
                />

                {/* Submit Button */}
                <button
                    onClick={handleVerify}
                    disabled={loading || token.length !== 6}
                    className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white p-3 rounded-lg font-medium hover:from-emerald-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-800 flex items-center justify-center"
                    aria-busy={loading}
                >
                    {loading ? (
                        <>
                            <Spinner size="sm" className="mr-2" />
                            <span>Verifying...</span>
                        </>
                    ) : (
                        <span>Verify</span>
                    )}
                </button>

                {/* Security Badge */}
                <div className="mt-8 pt-6 border-t border-gray-700">
                    <div className="flex items-center justify-center text-xs text-gray-400">
                        <Shield size={14} className="mr-2 text-green-400" />
                        <span>Protected with Two-Factor Authentication</span>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
};

export default Verify2FAPage;